import account from "@/store/modules/account";
import activityLog from "./modules/activity-log";
import billing from "./modules/billing";
import bucket from "./modules/bucket";
import bucketJourney from "./modules/bucket-journey";
import bucketOption from "./modules/bucket-option";
import callDisposition from "./modules/call-disposition";
import callLog from "./modules/call-log";
import company from "./modules/company";
import contact from "./modules/contact";
import contactTag from "./modules/contact-tag";
import email from "./modules/email";
import error from "./modules/error";
import fullenrich from "./modules/fullenrich";
import impersonation from "@/store/modules/impersonation";
import integration from "./modules/integration";
import leadSource from "./modules/lead-source";
import meeting from "./modules/meeting";
import mfa from "./modules/mfa";
import note from "./modules/note";
import notification from "./modules/notification";
import opportunity from "./modules/opportunity";
import opportunityContact from "./modules/opportunity-contact";
import opportunityStage from "./modules/opportunity-stage";
import organization from "./modules/organization";
import organizationSetting from "./modules/organization-setting";
import parentTemplateFolder from "./modules/parent-template-folder";
import phoneNumber from "./modules/phone-number";
import report from "./modules/report";
import search from "./modules/search";
import subscription from "./modules/subscription";
import tag from "./modules/tag";
import template from "./modules/template";
import templateFolder from "./modules/template-folder";
import ui from "./modules/ui";
import user from "./modules/user";
import { LOCAL_STORAGE_KEY, IMPERSONATION_SESSION_KEY } from "@/utils/const";
import { parseImpersonationToken } from "@/utils/parseImpersonationToken";
import { createStore } from "vuex";
import createVuexPersistedState from "vuex-persistedstate";

// Detect impersonation token in the URL at module-evaluation time (before store creation).
// This lets us switch the persistence storage to sessionStorage so the impersonated
// session never leaks into localStorage and never outlives the browser tab.
function detectImpersonationSession(): boolean {
  const token = new URLSearchParams(window.location.search).get("token");
  if (token) {
    const payload = parseImpersonationToken(token);
    if (payload?.impersonatedBy) {
      sessionStorage.setItem(IMPERSONATION_SESSION_KEY, "1");
      // Start fresh — don't rehydrate stale data from a prior impersonation.
      sessionStorage.removeItem(LOCAL_STORAGE_KEY);
      return true;
    }
  }
  // Also handle page refreshes within an ongoing impersonation session.
  return sessionStorage.getItem(IMPERSONATION_SESSION_KEY) === "1";
}

const isImpersonationSession = detectImpersonationSession();
const persistStorage = isImpersonationSession ? window.sessionStorage : window.localStorage;

export default createStore({
  state: {},
  getters: {},
  mutations: {},
  actions: {},
  modules: {
    account,
    impersonation,
    activityLog,
    billing,
    bucket,
    bucketJourney,
    bucketOption,
    callDisposition,
    callLog,
    company,
    leadSource,
    contact,
    contactTag,
    error,
    email,
    fullenrich,
    integration,
    meeting,
    mfa,
    note,
    notification,
    opportunity,
    opportunityContact,
    opportunityStage,
    organization,
    organizationSetting,
    parentTemplateFolder,
    phoneNumber,
    report,
    search,
    subscription,
    tag,
    template,
    templateFolder,
    ui,
    user,
  },
  plugins: [
    createVuexPersistedState({
      key: LOCAL_STORAGE_KEY,
      storage: persistStorage,
      paths: [
        "account",
        "impersonation",
        "bucketOption",
        "organizationSetting",
        "subscription",
        "billing",
        "notification.dismissedBannerTimestamps",
      ],
    }),
  ],
});
